<template>
  <blockquote class="quote">
    <SvgAsset
      :content="quoteSvg"
      class="quote__icon"
      :style="{ color: mainColor }"
    />

    <div class="quote__content">
      <div class="quote__citation">
        <TheContent
          v-for="item of citationContent"
          :key="item.id"
          :elem="item"
        />
      </div>
      <div
        v-if="authorContent"
        class="quote__author"
      >
        <TheContent
          v-for="item of authorContent"
          :key="item.id"
          :elem="item"
        />
      </div>
      <div
        v-if="aboutContent"
        class="quote__about"
      >
        <TheContent
          v-for="item of aboutContent"
          :key="item.id"
          :elem="item"
        />
      </div>
    </div>
  </blockquote>
</template>

<script lang="ts" setup>
  import { computed } from 'vue';
  import TheContent from '~/components/TheContent';
  import type { IBaseQuote, IBaseQuoteAttributes } from '~/types/components/BaseQuote';
  import { useSingleArticleStore } from '~/store/singleArticle';
  import SvgAsset from '@devhacker/shared/components/SvgAsset.vue';
  import quoteSvg from 'assets/images/svg/quote.svg?raw';

  const props = defineProps<{
    elem: IBaseQuote;
  }>();

  const singleArticleStore = useSingleArticleStore();

  const mainColor = computed(() => singleArticleStore.article?.meta?.main_color ?? '#C722CA');
  const attributes = computed((): IBaseQuoteAttributes => props.elem.attributes);
  const citationContent = computed(() => attributes.value.parsed_citation ?? props.elem.children);
  const authorContent = computed(() => attributes.value.parsed_author);
  const aboutContent = computed(() => attributes.value.parsed_about);
</script>

<style lang="scss" scoped>
  @use '~/scss/article' as *;

  .quote {
    width: 100%;
    font-family: $text-font--1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 8px 0;
    color: $text-color--2;

    @include tablet {
      flex-direction: row;
      flex-wrap: nowrap;
      align-items: flex-start;
      gap: 16px;
    }

    &__icon {
      max-width: 64px;
      max-height: 49px;
      height: 100%;
      flex-shrink: 0;
    }

    &__content {
      font-size: 18px;
      line-height: 28px;

      @include tablet {
        font-style: 22px;
        line-height: 32px;
      }

      :deep(a) {
        @include link-styles;
      }

      :deep(.paragraph) {
        font-size: inherit;
        line-height: inherit;
      }
    }

    .quote__citation {
      & > :deep(* + *) {
        margin-top: 12px;

        @include tablet {
          margin-top: 16px;
        }
      }
    }

    .quote__author {
      margin-top: 16px;
      font-size: 16px;
      line-height: 24px;
      font-weight: 500;
      color: #000;

      @include tablet {
        font-size: 18px;
        line-height: 28px;
      }
    }

    .quote__about {
      margin-top: 2px;
      color: $text-color--1;
      font-size: 14px;
      line-height: 20px;

      @include tablet {
        font-size: 16px;
        line-height: 24px;
      }
    }
  }
</style>
